<template>
  <v-card id="add-species-hits-dialog" max-height="70vh">
    <v-card-title class="primary white--text">
      <v-icon color="tertiary" class="mr-2">mdi-paw</v-icon>
      <span class="headline">{{ $t('addSpeciesHitsTitle') }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh hideFullscreen customCancel @close="$emit('cancel')"/>
    </v-card-title>

    <v-card-text class="pt-5">
      <div class="text-subtitle-2 mb-1">{{ $t('speciesFoundOnTract') }}</div>
      <p class="text-caption grey--text text--darken-1 mb-2">{{ $t('addSpeciesHitsHelp') }}</p>
      <v-sheet v-if="rows.length" outlined rounded class="px-3 pt-3 pb-1 mb-2">
        <v-row
        v-for="(row, index) in rows"
        :key="row.uid"
        dense
        class="align-center">
          <v-col
          v-if="speciesDisabled(row)"
          cols="12"
          sm="6"
          class="d-flex align-center pb-7">
            <v-icon small color="info" class="mr-2">mdi-information-outline</v-icon>
            <span class="text-caption grey--text text--darken-1">{{ $t('groupOnlyHitWillBeRecorded') }}</span>
          </v-col>
          <template v-else>
            <v-col cols="12" sm="3">
              <v-text-field
              v-model="row.commonName"
              :label="$t('commonName')"
              outlined
              dense
              :maxlength="maxNameLength"
              :error="nameRequired(row)"
              :error-messages="duplicateError(row)">
                <template #append>
                  <Icon
                  v-if="nameRequired(row)"
                  icon="mdi-alert"
                  iconColor="error"
                  margin="mr-0"
                  style="line-height: 1.25rem"
                  :tooltipText="$t('speciesNameRequiredForGroup')"/>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
              v-model="row.scientificName"
              :label="$t('scientificName')"
              outlined
              dense
              :maxlength="maxNameLength"/>
            </v-col>
          </template>
          <v-col cols="12" sm="4">
            <v-select
            v-model="row.mitigationGroupId"
            :items="groups"
            item-value="mitigationGroupId"
            item-text="name"
            :label="$t('selectMitigationGroup')"
            :menu-props="{ maxWidth: menuWidth }"
            outlined
            dense
            :error-messages="groupError(row)"
            @focus="measureMenuWidth"
            @change="clearNamesForNonSpeciesGroup(row)">
              <template #selection="{ item: groupOption }">
                <v-icon :color="groupOption.color" small class="mr-2">mdi-square</v-icon>
                {{ groupOption.name }}
              </template>
              <template #item="{ item: groupOption }">
                <v-list-item-content>
                  <v-list-item-title class="d-flex align-center">
                    <v-icon :color="groupOption.color" small class="mr-2">mdi-square</v-icon>
                    {{ groupOption.name }}
                  </v-list-item-title>
                  <div
                  v-if="expandedGroupDescriptionId === groupOption.mitigationGroupId"
                  class="mt-1"
                  style="white-space: normal;">
                    <div class="dd-eyebrow">{{ $t('groupDescription') }}</div>
                    <div class="text-caption grey--text text--darken-2">{{ groupOption.description || $t('noGroupDescription') }}</div>
                  </div>
                </v-list-item-content>
                <v-list-item-action class="my-0">
                  <v-btn icon small @click.stop="toggleGroupDescription(groupOption.mitigationGroupId)">
                    <v-icon color="info" small>{{ expandedGroupDescriptionId === groupOption.mitigationGroupId ? 'mdi-information' : 'mdi-information-outline' }}</v-icon>
                  </v-btn>
                </v-list-item-action>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" sm="2" class="d-flex justify-end pb-7">
            <v-tooltip v-if="!speciesDisabled(row)" bottom :color="canCheckNatureServe(row) ? 'primary' : 'grey'">
              <template #activator="{ on }">
                <span v-on="on">
                  <v-btn
                  icon
                  small
                  color="primary"
                  :disabled="!canCheckNatureServe(row)"
                  @click="checkNatureServe(row)">
                    <v-icon small>mdi-database-search-outline</v-icon>
                  </v-btn>
                </span>
              </template>
              <span class="white--text text-subtitle-2">{{ canCheckNatureServe(row) ? $t('checkNatureServe') : $t('natureServeNameRequired') }}</span>
            </v-tooltip>
            <v-btn icon small color="error" :title="$t('removeSpecies')" @click="removeRow(index)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-sheet>
      <v-btn small text color="primary" @click="addRow">
        <v-icon small left>mdi-plus</v-icon>
        {{ $t('addSpecies') }}
      </v-btn>
    </v-card-text>

    <v-card-actions class="px-6 pb-4">
      <v-btn text outlined color="black" @click="$emit('cancel')">{{ $t('cancel') }}</v-btn>
      <v-spacer/>
      <v-btn color="success" :disabled="!canSave" @click="save">
        <v-icon left>mdi-content-save</v-icon>
        {{ $t('saveAndClose') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { StringConstants } from '@/utils/constants'
import { menuWidthFromEvent } from '@/utils/menuWidth.js'
import { canCheckNatureServe, backfillSpeciesNames } from '@/utils/dueDiligence/presentation.js'
import { normalizeSpeciesHit, speciesHitKey, duplicateSpeciesHitKeys } from '@/utils/dueDiligence/speciesHits.js'

export default {
  name: 'AddSpeciesHitsDialog',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    groups: {
      type: Array,
      required: true
    },
    initialRows: {
      type: Array,
      default: () => []
    },
    // Resolved { commonName, scientificName } from the NatureServe slide-over; back-fills the
    // empty name field of whichever row triggered the lookup.
    natureServeResult: {
      type: Object,
      default: null
    }
  },

  data () {
    const seedRows = this.initialRows.length ? this.initialRows : [{}]
    return {
      rows: seedRows.map((row, index) => ({
        uid: index,
        commonName: row.commonName ?? '',
        scientificName: row.scientificName ?? '',
        mitigationGroupId: row.mitigationGroupId ?? null
      })),
      nextUid: seedRows.length,
      pendingNatureServeUid: null,
      expandedGroupDescriptionId: null,
      menuWidth: undefined,
      maxNameLength: StringConstants.ExtraLongStringLength
    }
  },

  watch: {
    natureServeResult (result) {
      if (!result || this.pendingNatureServeUid == null) return
      const row = this.rows.find(r => r.uid === this.pendingNatureServeUid)
      this.pendingNatureServeUid = null
      backfillSpeciesNames(row, result)
    }
  },

  computed: {
    groupsById () {
      return new Map(this.groups.map(g => [g.mitigationGroupId, g]))
    },

    duplicateKeysByGroup () {
      const byGroup = new Map()
      this.rows.filter(this.hasName).forEach(row => {
        if (!byGroup.has(row.mitigationGroupId)) byGroup.set(row.mitigationGroupId, [])
        byGroup.get(row.mitigationGroupId).push(row)
      })
      return new Map([...byGroup.entries()].map(([groupId, rows]) => [groupId, duplicateSpeciesHitKeys(rows)]))
    },

    canSave () {
      return this.rows.length > 0 && this.rows.every(row => this.rowValid(row) && !this.rowDuplicated(row))
    }
  },

  methods: {
    canCheckNatureServe,

    checkNatureServe (row) {
      this.pendingNatureServeUid = row.uid
      this.$emit('check-nature-serve', { common: row.commonName, scientific: row.scientificName })
    },

    groupFor (row) {
      return this.groupsById.get(row.mitigationGroupId) ?? null
    },

    hasName (row) {
      return !!(row.commonName.trim() || row.scientificName.trim())
    },

    speciesDisabled (row) {
      const group = this.groupFor(row)
      return !!group && !group.requiresHabitat
    },

    clearNamesForNonSpeciesGroup (row) {
      if (!this.speciesDisabled(row)) return
      row.commonName = ''
      row.scientificName = ''
    },

    rowValid (row) {
      const group = this.groupFor(row)
      if (!group) return false
      return this.hasName(row) || !group.requiresHabitat
    },

    groupError (row) {
      return row.mitigationGroupId != null ? [] : [this.$t('mitigationGroupRequired')]
    },

    nameRequired (row) {
      const group = this.groupFor(row)
      return !!group?.requiresHabitat && !this.hasName(row)
    },

    rowDuplicated (row) {
      if (!this.hasName(row)) return false
      return !!this.duplicateKeysByGroup.get(row.mitigationGroupId)?.has(speciesHitKey(row))
    },

    duplicateError (row) {
      return this.rowDuplicated(row) ? [this.$t('duplicateSpeciesInGroup')] : []
    },

    toggleGroupDescription (id) {
      this.expandedGroupDescriptionId = this.expandedGroupDescriptionId === id ? null : id
    },

    measureMenuWidth (event) {
      this.menuWidth = menuWidthFromEvent(event)
    },

    addRow () {
      this.rows.push({ uid: this.nextUid++, commonName: '', scientificName: '', mitigationGroupId: null })
    },

    removeRow (index) {
      this.rows.splice(index, 1)
    },

    save () {
      if (!this.canSave) return
      const byGroup = new Map()
      for (const row of this.rows) {
        if (!byGroup.has(row.mitigationGroupId)) byGroup.set(row.mitigationGroupId, [])
        if (this.hasName(row)) byGroup.get(row.mitigationGroupId).push(normalizeSpeciesHit(row))
      }
      this.$emit('save', [...byGroup.entries()].map(([mitigationGroupId, speciesHits]) => ({
        mitigationGroupId,
        speciesHits
      })))
    }
  }
}
</script>
